/**
 * ASP.NET's JavaScriptSerializer emits dates as `"/Date(1690800000000)/"`.
 *
 * The legacy screens unwrapped these with
 * `new XDate(Date.parse(new Date(parseInt(d.replace('/Date(', '')))))` and
 * formatted them with XDate. Both jobs are done here so the XDate dependency
 * can be dropped.
 */

const DOTNET_DATE_RE = /\/Date\((-?\d+)([+-]\d{4})?\)\//

export function parseDotNetDate(value: string | null | undefined): Date | null {
  if (!value) return null

  const match = DOTNET_DATE_RE.exec(value)
  if (match) {
    const ms = Number(match[1])
    return Number.isNaN(ms) ? null : new Date(ms)
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const pad = (n: number) => String(n).padStart(2, '0')

/** "17 Aug 2024 14:18:05" — the `dd MMM yyyy HH:mm:ss` format the legacy code used. */
export function formatDateTime(value: string | null | undefined): string {
  const date = parseDotNetDate(value)
  if (!date) return ''
  return (
    `${pad(date.getDate())} ${MONTHS[date.getMonth()]} ${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  )
}

/** "17-Aug-2024 14:18" — the `dd-MMM-yyyy HH:mm` variant used by follow-ups. */
export function formatShortDateTime(value: string | null | undefined): string {
  const date = parseDotNetDate(value)
  if (!date) return ''
  return (
    `${pad(date.getDate())}-${MONTHS[date.getMonth()]}-${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

/** Parses the `[{ "fuDisplayName": "…" }]` JSON strings on a physical appointment. */
export function parseDisplayNameList(raw: string | null | undefined): string {
  if (!raw) return ''
  try {
    const parsed = JSON.parse(raw) as Array<{ fuDisplayName?: string }>
    if (!Array.isArray(parsed)) return ''
    return parsed
      .map((item) => item.fuDisplayName ?? '')
      .filter(Boolean)
      .join(', ')
  } catch {
    return ''
  }
}
